import React, { useState, useEffect } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SENDREPLY, UPDATE_DISPUTE, GET_HELP_DETAIL } from "../../../../services/ApiCalls";
import { toast } from "react-toastify";
import fileUploader from "../../../../utilities/fileUploader";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import Loader from "../../../../components/Common/Loader";
import moment from "moment";

const HelpDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [helpData, setHelpData] = useState(null);
  const [loader, setLoader] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [modal, setModal] = useState(false);
  const [message, setMessage] = useState("");
  const [imgLoader, setImgLoader] = useState(false);

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await GET_HELP_DETAIL(id);
    const success = checkResponse({ res, setData: setHelpData, setLoader }, setLoader);
    if (success && res?.data?.data) {
      setSelectedStatus(res.data.data.status);
    }
  });

  useEffect(() => {
    if (id) {
      getData();
    }
  }, [id]);

  const toggleModal = () => setModal(!modal);

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleSend = async () => {
    const payload = {
      id: helpData?._id,
      message,
      attachments: imagePreviews,
    };
    try {
      let res = await SENDREPLY(payload);
      if (res?.data?.status === "success" || res?.data?.success === true) {
        toast.success(res?.data?.message || t("helpSupport.detail.messages.replySentSuccessfully"));
        setMessage("");
        setImagePreviews([]);
        setModal(false);
        // Refresh data to show new reply
        getData();
      } else {
        toast.error(res?.data?.message);
      }
    } catch (error) {
      toast.error(t("helpSupport.detail.messages.somethingWentWrong"));
    }
  };

  const handleDisputeStatus = async () => {
    try {
      let response = await UPDATE_DISPUTE({
        status: selectedStatus,
        ticketId: helpData?._id,
      });
      if (response?.data?.status === "success") {
        toast.success(response?.data?.message);
        navigate("/help");
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      toast.error(t("helpSupport.detail.messages.somethingWentWrong"));
    }
  };

  const handleImageChange = async (e) => {
    setImgLoader(true);
    const files = Array.from(e.target.files);

    const uploadedUrls = [];
    for (let file of files) {
      const res = await fileUploader(file);
      if (res?.url) {
        uploadedUrls.push(res.url);
      }
    }

    setImagePreviews((prev) => [...prev, ...uploadedUrls]);
    setImgLoader(false);
  };

  const handleRemoveImage = (index) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  if (loader) {
    return <Loader />;
  }

  if (!helpData) {
    return (
      <section className="helpDetail py-3 position-relative">
        <Container>
          <div className="text-center py-5">
            <p>{t("helpSupport.detail.noDataFound")}</p>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className="helpDetail py-3 position-relative">
      <Container>
        <Modal isOpen={modal} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>{t("helpSupport.detail.modal.sendReply")}</ModalHeader>
          <ModalBody>
            <label className="form-label fw-bold text-muted ps-2 m-0">
              {t("helpSupport.detail.modal.writeMessage")}
            </label>
            <Input
              type="textarea"
              placeholder={t("helpSupport.detail.modal.messagePlaceholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />

            <Col lg="12" className="my-2">
              <div className="py-2">
                <label className="form-label fw-bold text-muted ps-2 m-0">
                  {t("helpSupport.detail.modal.uploadImages")}
                </label>

                <div
                  className="border border-2 border-dashed rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light"
                  style={{ cursor: "pointer", minHeight: "140px" }}
                  onClick={() =>
                    document.getElementById("imageUploadInput").click()
                  }
                >
                  <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                  <p className="m-0 text-muted">
                    {t("helpSupport.detail.modal.clickOrDragDrop")}
                  </p>
                  <input
                    id="imageUploadInput"
                    type="file"
                    accept="image/*"
                    multiple
                    className="d-none"
                    onChange={handleImageChange}
                  />
                </div>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  {imgLoader ? (
                    <p>{t("helpSupport.detail.modal.uploading")}</p>
                  ) : (
                    imagePreviews?.map((src, i) => (
                      <div
                        key={i}
                        style={{
                          width: 120,
                          height: 120,
                          position: "relative",
                        }}
                        className="border rounded overflow-hidden shadow-sm"
                      >
                        <img
                          src={src}
                          alt={`preview-${i}`}
                          className="w-100 h-100 object-fit-cover"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                          onClick={() => handleRemoveImage(i)}
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Col>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleSend}
              disabled={!message.trim()}
            >
              {t("helpSupport.detail.modal.send")}
            </Button>
            <Button color="secondary" onClick={toggleModal}>
              {t("helpSupport.detail.modal.cancel")}
            </Button>
          </ModalFooter>
        </Modal>

        <Row>
          <Col lg="12">
            <div className="d-flex align-items-center gap-10">
              <Link
                to="/help"
                className="border d-flex align-items-center p-2 rounded"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 10 16"
                  fill="none"
                >
                  <path
                    d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                    fill="#E8307A"
                    stroke="#E8307A"
                    strokeWidth="0.2"
                  />
                </svg>
              </Link>
              <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                {t("helpSupport.detail.title")}
              </h4>
            </div>
          </Col>

          <Col lg="12" className="my-2">
            <div
              className="formWrpper px-lg-5 p-md-4 p-3 rounded"
              style={{ background: "#EEEEEE" }}
            >
              <Row className="justify-content-between">
                <Col md={6} className="my-2">
                  <ul className="list-unstyled ps-0 mb-0">
                    <li className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold ">{t("helpSupport.detail.name")}</p>
                      <h6 className="m-0 text-muted fw-bold ">
                        {helpData?.raisedBy?.first_name + " " + helpData?.raisedBy?.last_name}
                      </h6>
                    </li>
                    <li className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold ">{t("helpSupport.detail.email")}</p>
                      <h6 className="m-0 text-muted fw-bold  text-break">
                        {helpData?.raisedBy?.email}
                      </h6>
                    </li>
                    <li className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold ">{t("helpSupport.detail.status")}</p>
                      <h6 className="m-0 text-muted fw-bold ">
                        {helpData?.status}
                      </h6>
                    </li>
                  </ul>
                </Col>

                <Col md={6} className="my-2">
                  <ul className="list-unstyled mb-0">
                    <li className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold">{t("helpSupport.detail.category")}</p>
                      <p className="m-0 text-muted fw-bold">
                        {helpData?.category}
                      </p>
                    </li>
                    <li className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold">{t("helpSupport.detail.role")}</p>
                      <h6 className="m-0 text-muted fw-bold">
                        {helpData?.role}
                      </h6>
                    </li>
                    <li className="py-3 d-flex align-items-start gap-10">
                      <p className="m-0 themeBlue fw-sbold">
                        {t("helpSupport.detail.description")}
                      </p>
                      <p
                        className="m-0 text-muted"
                        style={{ fontSize: 12, lineHeight: "18px" }}
                      >
                        {helpData?.description}
                      </p>
                    </li>
                  </ul>
                </Col>

                <button
                  onClick={toggleModal}
                  className="replyBtn commonBtn GreyBtn w-auto mx-auto"
                >
                  {t("helpSupport.detail.reply")}
                </button>
              </Row>
            </div>
          </Col>

          {/* Replies Section */}
          {helpData?.replies && helpData.replies.length > 0 && (
            <Col lg="12" className="my-4">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <h5 className="mb-4 fw-bold text-dark">{t("helpSupport.detail.replyHistory")}</h5>
                <div
                  style={{
                    maxHeight: "600px",
                    overflowY: "auto",
                    paddingRight: "10px",
                  }}
                >
                  {helpData.replies.map((reply, index) => {
                      const isAdmin =
                        reply.senderModel === "admin" ||
                        reply.senderModel === "user";
                      const isHotelOwner = reply.senderModel === "hotelowner";

                      return (
                        <div
                          key={reply._id || index}
                          className={`mb-3 d-flex justify-content-end`}
                        >
                          <div
                            className={`rounded p-3 shadow-sm ${
                              isAdmin
                                ? "bg-primary text-white"
                                : "bg-white border"
                            }`}
                            style={{
                              maxWidth: "75%",
                              wordBreak: "break-word",
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                              <span
                                className={`badge ${
                                  isAdmin
                                    ? "bg-light text-primary"
                                    : "bg-secondary text-white"
                                }`}
                                style={{ fontSize: "11px" }}
                              >
                                {isAdmin
                                  ? t("helpSupport.detail.sender.admin")
                                  : isHotelOwner
                                  ? t("helpSupport.detail.sender.hotelOwner")
                                  : reply.senderModel}
                              </span>
                              <span
                                className={`small ${
                                  isAdmin ? "text-white-50" : "text-muted"
                                }`}
                              >
                                {moment(reply.createdAt).format(
                                  "MMM DD, YYYY hh:mm A"
                                )}
                              </span>
                            </div>
                            {reply.message && (
                              <p
                                className={`mb-0 ${
                                  isAdmin ? "text-white" : "text-dark"
                                }`}
                                style={{ fontSize: "14px", lineHeight: "1.5" }}
                              >
                                {reply.message}
                              </p>
                            )}
                            {reply.attachments &&
                              reply.attachments.length > 0 && (
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                  {reply.attachments.map(
                                    (attachment, attIndex) => (
                                      <div
                                        key={attIndex}
                                        className="border rounded overflow-hidden shadow-sm"
                                        style={{
                                          width: "120px",
                                          height: "120px",
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          window.open(attachment, "_blank")
                                        }
                                      >
                                        <img
                                          src={attachment}
                                          alt={`attachment-${attIndex}`}
                                          className="w-100 h-100 object-fit-cover"
                                        />
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </Col>
          )}

          {helpData?.status?.toLowerCase() !== "closed" && (
            <>
              <Col lg="6" className="my-2">
                <ul className="list-unstyled ps-0 mb-0">
                  <li className="py-3 d-flex align-items-start gap-10">
                    <p className="m-0 themeBlue fw-sbold w-25">
                      {t("helpSupport.detail.markStatusAs")}
                    </p>
                    <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10">
                      {["Open", "In Progress", "Resolved", "Closed"].map(
                        (status) => {
                          const statusKeyMap = {
                            "Open": "open",
                            "In Progress": "inProgress",
                            "Resolved": "resolved",
                            "Closed": "closed"
                          };
                          const statusKey = statusKeyMap[status] || status.toLowerCase();
                          return (
                            <li
                              key={status}
                              className="d-flex align-items-center gap-2"
                            >
                              <input
                                name="status"
                                type="radio"
                                id={status}
                                value={status}
                                checked={selectedStatus === status}
                                onChange={handleStatusChange}
                              />
                              <label
                                htmlFor={status}
                                className="form-label m-0 fw-sbold text-muted text-nowrap"
                              >
                                {t(`helpSupport.status.${statusKey}`, status)}
                              </label>
                            </li>
                          );
                        }
                      )}
                    </ul>
                  </li>
                </ul>
              </Col>

              <Col lg="12" className="my-2">
                <div className="text-center mt-3">
                  <Button
                    type="button"
                    className="commonBtn"
                    onClick={handleDisputeStatus}
                  >
                    {t("helpSupport.detail.updateStatus")}
                  </Button>
                </div>
              </Col>
            </>
          )}
        </Row>
      </Container>
    </section>
  );
};

export default HelpDetail;
